
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
    Button
} from "@mui/material";

import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import ConfirmDialog from "../../Common/ConfirmDialog";

export default function History({
    resultDataMap,
    setResultDataMap,
    resultFields = [],
    rows = [],
    showHistory = true,
    showCalibrationGraph = true,
    onPredict
}) {

    const [graphVisible, setGraphVisible] = useState(
        showCalibrationGraph
    );

    const [selectedRun, setSelectedRun] = useState(null);

    const [openClearDialog, setOpenClearDialog] = useState(false);

    const resultPanelRef = useRef(null);


    /*
     * =========================================================
     * HISTORY DATA
     * =========================================================
     */

    const historyData = useMemo(
        () => Object.values(resultDataMap || {}),
        [resultDataMap]
    );


    /*
     * =========================================================
     * SELECTED HISTORY ENTRY
     * =========================================================
     */

    const selectedEntry = useMemo(() => {

        if (
            selectedRun === null ||
            selectedRun === undefined ||
            historyData.length === 0
        ) {
            return null;
        }

        return (
            historyData.find(
                entry =>
                    String(entry.run) ===
                    String(selectedRun)
            ) || null
        );

    }, [
        historyData,
        selectedRun
    ]);


    /*
     * =========================================================
     * SELECTED RESULT
     * =========================================================
     */

    const selectedResult =
        selectedEntry?.result || null;

    const selectedDescription =
        selectedEntry?.description || "";


    /*
     * =========================================================
     * CALIBRATION ROWS
     *
     * IMPORTANT:
     *
     * Prefer rows stored inside the selected history entry.
     * Fall back to the rows passed from CalibrationLayout.
     *
     * This makes the graph work both:
     *
     * 1. Immediately after calibration
     * 2. When selecting an older history run
     * =========================================================
     */

    const selectedRows = useMemo(() => {

        if (
            Array.isArray(selectedEntry?.rows) &&
            selectedEntry.rows.length > 0
        ) {
            return selectedEntry.rows;
        }

        if (Array.isArray(rows)) {
            return rows;
        }

        return [];

    }, [
        selectedEntry,
        rows
    ]);


    /*
     * =========================================================
     * RESULT FIELDS
     * =========================================================
     */

    const fields =
        resultFields.length > 0
            ? resultFields
            : Object.keys(selectedResult || {});


    /*
     * =========================================================
     * PREDICTIONS
     * =========================================================
     */

    const predictions =
        Array.isArray(
            selectedResult?.predictions
        )
            ? selectedResult.predictions
            : null;


    /*
     * =========================================================
     * AUTOMATICALLY SELECT LATEST RUN
     *
     * Also shows graph and scrolls to result.
     * =========================================================
     */

    useEffect(() => {

        if (historyData.length === 0) {

            setSelectedRun(null);
            setGraphVisible(false);

            return;
        }


        const latestEntry =
            historyData.reduce(
                (latest, entry) => {

                    if (!latest) {
                        return entry;
                    }

                    return Number(entry.run) >
                        Number(latest.run)
                        ? entry
                        : latest;

                },
                null
            );


        if (
            latestEntry &&
            latestEntry.run !== undefined
        ) {

            setSelectedRun(
                latestEntry.run
            );

            setGraphVisible(
                showCalibrationGraph
            );

        }


        const timer = setTimeout(() => {

            resultPanelRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });

        }, 150);


        return () => clearTimeout(timer);

    }, [
        historyData,
        showCalibrationGraph
    ]);


    /*
     * =========================================================
     * PARSE RESPONSE VALUES
     *
     * Supports:
     *
     * 1.23
     * "1.23"
     * "1.23,1.24,1.25"
     * "1.23 1.24 1.25"
     * ["1.23", "1.24"]
     *
     * IMPORTANT:
     * 0 is preserved.
     * =========================================================
     */

    const parseResponses = (value) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return [];
        }


        if (Array.isArray(value)) {

            return value
                .filter(
                    v =>
                        v !== null &&
                        v !== undefined &&
                        String(v).trim() !== ""
                )
                .map(v => Number(v))
                .filter(
                    v => Number.isFinite(v)
                );

        }


        return String(value)
            .split(/[,\s]+/)
            .map(v => v.trim())
            .filter(v => v !== "")
            .map(v => Number(v))
            .filter(
                v => Number.isFinite(v)
            );
    };


    /*
     * =========================================================
     * RESP1 DATA
     *
     * X = Response 1
     * Y = SAV
     * =========================================================
     */

    const resp1Data = useMemo(() => {

        if (!Array.isArray(selectedRows)) {
            return [];
        }


        const data = [];


        selectedRows.forEach(row => {

            if (!row) {
                return;
            }


            /*
             * SAV = 0 is valid.
             */
            if (
                row.sav === null ||
                row.sav === undefined ||
                String(row.sav).trim() === ""
            ) {
                return;
            }


            const sav =
                Number(row.sav);


            if (!Number.isFinite(sav)) {
                return;
            }


            const responses =
                parseResponses(
                    row.resp1
                );


            responses.forEach(
                response => {

                    data.push([
                        response,
                        sav
                    ]);

                }
            );

        });


        return data;

    }, [selectedRows]);


    /*
     * =========================================================
     * RESP2 DATA
     *
     * X = Response 2
     * Y = SAV
     * =========================================================
     */

    const resp2Data = useMemo(() => {

        if (!Array.isArray(selectedRows)) {
            return [];
        }


        const data = [];


        selectedRows.forEach(row => {

            if (!row) {
                return;
            }


            /*
             * SAV = 0 is valid.
             */
            if (
                row.sav === null ||
                row.sav === undefined ||
                String(row.sav).trim() === ""
            ) {
                return;
            }


            const sav =
                Number(row.sav);


            if (!Number.isFinite(sav)) {
                return;
            }


            const responses =
                parseResponses(
                    row.resp2
                );


            responses.forEach(
                response => {

                    data.push([
                        response,
                        sav
                    ]);

                }
            );

        });


        return data;

    }, [selectedRows]);


    /*
     * =========================================================
     * TRENDLINE CALCULATION
     * =========================================================
     */

    const calculateTrendline = (
        data
    ) => {

        if (
            !Array.isArray(data) ||
            data.length < 2
        ) {

            return {
                line: [],
                slope: 0,
                intercept: 0,
                r2: 0
            };

        }


        const n = data.length;


        const sumX =
            data.reduce(
                (sum, point) =>
                    sum + point[0],
                0
            );


        const sumY =
            data.reduce(
                (sum, point) =>
                    sum + point[1],
                0
            );


        const sumXY =
            data.reduce(
                (sum, point) =>
                    sum +
                    point[0] *
                    point[1],
                0
            );


        const sumX2 =
            data.reduce(
                (sum, point) =>
                    sum +
                    point[0] *
                    point[0],
                0
            );


        const denominator =
            n * sumX2 -
            sumX * sumX;


        /*
         * All X values are identical.
         */
        if (denominator === 0) {

            return {
                line: [],
                slope: 0,
                intercept: 0,
                r2: 0
            };

        }


        const slope =
            (
                n * sumXY -
                sumX * sumY
            ) /
            denominator;


        const intercept =
            (
                sumY -
                slope * sumX
            ) /
            n;


        /*
         * R�
         */

        const meanY =
            sumY / n;


        const ssTot =
            data.reduce(
                (sum, point) =>
                    sum +
                    Math.pow(
                        point[1] -
                        meanY,
                        2
                    ),
                0
            );


        const ssRes =
            data.reduce(
                (sum, point) => {

                    const predicted =
                        slope *
                        point[0] +
                        intercept;


                    return (
                        sum +
                        Math.pow(
                            point[1] -
                            predicted,
                            2
                        )
                    );

                },
                0
            );


        const r2 =
            ssTot === 0
                ? 1
                : 1 -
                ssRes / ssTot;


        /*
         * Trendline endpoints.
         */

        const xValues =
            data.map(
                point => point[0]
            );


        const minX =
            Math.min(...xValues);


        const maxX =
            Math.max(...xValues);


        const line = [
            [
                minX,
                slope * minX +
                intercept
            ],
            [
                maxX,
                slope * maxX +
                intercept
            ]
        ];


        return {
            line,
            slope,
            intercept,
            r2
        };

    };


    /*
     * =========================================================
     * TRENDLINES
     * =========================================================
     */

    const resp1Trend =
        useMemo(
            () =>
                calculateTrendline(
                    resp1Data
                ),
            [resp1Data]
        );


    const resp2Trend =
        useMemo(
            () =>
                calculateTrendline(
                    resp2Data
                ),
            [resp2Data]
        );


    /*
     * =========================================================
     * FORMAT NUMBER
     * =========================================================
     */

    const formatNumber = (
        value
    ) => {

        if (
            !Number.isFinite(value)
        ) {
            return "0";
        }


        return Number(value)
            .toFixed(6);

    };


    /*
     * =========================================================
     * CHART OPTIONS
     * =========================================================
     */

    const chartOptions =
        useMemo(() => {

            const getEquation = (
                label,
                trend
            ) => {

                if (
                    !trend ||
                    !Number.isFinite(
                        trend.slope
                    )
                ) {
                    return "";
                }


                return (
                    `${label}: y = ` +
                    `${formatNumber(
                        trend.slope
                    )} x ` +
                    `${trend.intercept >= 0
                        ? "+"
                        : "-"
                    } ` +
                    `${formatNumber(
                        Math.abs(
                            trend.intercept
                        )
                    )}`
                );

            };


            return {

                chart: {
                    type: "scatter",
                    zoomType: "xy",
                    height: 500,

                    // More room between title/equations and plot
                    spacingTop: 75,

                    spacingRight: 25,
                    spacingBottom: 55,
                    spacingLeft: 65,

                    events: {

                        render: function () {

                            const chart =
                                this;


                            /*
                             * Remove old equation label.
                             */

                            if (
                                chart.equationLabel
                            ) {

                                chart.equationLabel.destroy();

                                chart.equationLabel =
                                    null;

                            }


                            const equations =
                                [];


                            if (
                                resp1Data.length >= 2
                            ) {

                                equations.push(
                                    getEquation(
                                        "Resp1",
                                        resp1Trend
                                    )
                                );

                            }


                            if (
                                resp2Data.length >= 2
                            ) {

                                equations.push(
                                    getEquation(
                                        "Resp2",
                                        resp2Trend
                                    )
                                );

                            }


                            if (
                                equations.length === 0
                            ) {
                                return;
                            }


                            chart.equationLabel =
                                chart.renderer
                                    .text(
                                        equations.join(
                                            "     |     "
                                        ),
                                        0,
                                        0
                                    )
                                    .css({
                                        fontSize:
                                            "13px",
                                        fontWeight:
                                            "bold"
                                    })
                                    .add();


                            const bbox =
                                chart.equationLabel
                                    .getBBox();


                            const x =
                                chart.plotLeft +
                                (
                                    chart.plotWidth -
                                    bbox.width
                                ) / 2;


                            const y =
                                chart.plotTop -
                                20;


                            chart.equationLabel.attr({
                                x,
                                y
                            });

                        }
                    }
                },


                title: {
                    text: "Cal Curve",
                    margin: 25,

                    y: 5
                },


                //subtitle: {
                //    text:
                //        selectedEntry
                //            ? `Run ${selectedEntry.run}${selectedDescription
                //                ? ` - ${selectedDescription}`
                //                : ""
                //            }`
                //            : ""
                //},


                xAxis: {

                    title: {
                        text: "Response",
                        margin: 15
                    },
                    startOnTick: true,
                    endOnTick: true,
                    minPadding: 0.05,
                    maxPadding: 0.05,

                    gridLineWidth: 1

                },


                yAxis: {

                    title: {
                        text: "SAV",
                        margin: 15
                    },
                    startOnTick: true,
                    endOnTick: true,
                    minPadding: 0.05,
                    maxPadding: 0.05,
                    gridLineWidth: 1

                },


                legend: {
                    enabled: true
                },


                tooltip: {

                    useHTML: true,

                    formatter: function () {

                        return `
                            <b>${this.series.name}</b>
                            <br/>
                            Response: ${this.x}
                            <br/>
                            SAV: ${this.y}
                        `;

                    }

                },


                plotOptions: {

                    scatter: {

                        marker: {
                            radius: 5,
                            symbol: "circle"
                        }

                    },

                    series: {
                        animation: false
                    }

                },


                series: [

                    /*
                     * Response 1
                     */

                    {
                        name: "Resp1",
                        type: "scatter",
                        data: resp1Data,

                        marker: {
                            radius: 5,
                            symbol: "circle"
                        }
                    },


                    /*
                     * Response 1 trendline
                     */

                    {
                        name:
                            "Resp1 Trendline",

                        type: "line",

                        data:
                            resp1Trend.line,

                        marker: {
                            enabled: false
                        },

                        enableMouseTracking:
                            false,

                        dashStyle:
                            "Dash",

                        lineWidth: 2
                    },


                    /*
                     * Response 2
                     */

                    {
                        name: "Resp2",
                        type: "scatter",
                        data: resp2Data,

                        marker: {
                            radius: 5,
                            symbol: "circle"
                        }
                    },


                    /*
                     * Response 2 trendline
                     */

                    {
                        name:
                            "Resp2 Trendline",

                        type: "line",

                        data:
                            resp2Trend.line,

                        marker: {
                            enabled: false
                        },

                        enableMouseTracking:
                            false,

                        dashStyle:
                            "Dash",

                        lineWidth: 2
                    }

                ],


                credits: {
                    enabled: false
                }

            };

        }, [
            resp1Data,
            resp2Data,
            resp1Trend,
            resp2Trend,
            selectedEntry,
            selectedDescription
        ]);


    /*
     * =========================================================
     * CLEAR HISTORY
     * =========================================================
     */

    const handleClearHistory = () => {

        /*
         * Correct MicroSlide history key.
         */

        sessionStorage.removeItem(
            "microslideHistory_calibrate"
        );


        setResultDataMap({});


        setSelectedRun(null);


        setGraphVisible(false);


        setOpenClearDialog(false);

    };


    /*
     * =========================================================
     * SELECT RUN
     * =========================================================
     */

    const handleSelectRun = (
        entry
    ) => {

        if (!entry) {
            return;
        }


        setSelectedRun(
            entry.run
        );


        setGraphVisible(
            showCalibrationGraph
        );

    };


    /*
     * =========================================================
     * PREDICT
     * =========================================================
     */

    const handlePredict = (
        event,
        entry
    ) => {

        event.stopPropagation();


        if (onPredict) {
            onPredict(entry);
        }

    };


    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (

        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2
            }}
        >

            {/* =================================================
                RESULT + HISTORY
            ================================================== */}

            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        md: "row"
                    },
                    gap: 2,
                    alignItems: "stretch"
                }}
            >

                {/* =================================================
                    RESULT
                ================================================== */}

                <Paper
                    ref={resultPanelRef}
                    sx={{
                        flex: showHistory
                            ? 1
                            : "none",

                        width:
                            showHistory
                                ? "auto"
                                : "100%",

                        minWidth: 0,

                        p: 2,

                        minHeight: 300
                    }}
                >

                    <Typography
                        variant="h6"
                        gutterBottom
                    >
                        Result
                        {selectedEntry
                            ? ` (${selectedEntry.description ||
                            ""
                            })`
                            : ""
                        }
                    </Typography>


                    {selectedResult ? (

                        <TableContainer>

                            <Table
                                size="small"
                            >

                                <TableBody>

                                    {fields.map(
                                        field => (

                                            <TableRow
                                                key={field}
                                            >

                                                <TableCell>
                                                    <strong>
                                                        {
                                                            field
                                                        }
                                                    </strong>
                                                </TableCell>

                                                <TableCell>
                                                    {
                                                        selectedResult?.[
                                                        field
                                                        ] ??
                                                        "-"
                                                    }
                                                </TableCell>

                                            </TableRow>

                                        )
                                    )}

                                </TableBody>

                            </Table>


                            {/* =================================
                                PREDICTION RESULT
                            ================================== */}

                            {predictions &&
                                predictions.length >
                                0 && (

                                    <Box
                                        sx={{
                                            mt: 3
                                        }}
                                    >

                                        <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                        >
                                            Prediction Results
                                        </Typography>


                                        <TableContainer>

                                            <Table
                                                size="small"
                                            >

                                                <TableHead>

                                                    <TableRow>

                                                        {Object.keys(
                                                            predictions[0] ||
                                                            {}
                                                        ).map(
                                                            key => (

                                                                <TableCell
                                                                    key={key}
                                                                >
                                                                    <strong>
                                                                        {
                                                                            key
                                                                        }
                                                                    </strong>
                                                                </TableCell>

                                                            )
                                                        )}

                                                    </TableRow>

                                                </TableHead>


                                                <TableBody>

                                                    {predictions.map(
                                                        (
                                                            prediction,
                                                            index
                                                        ) => (

                                                            <TableRow
                                                                key={
                                                                    index
                                                                }
                                                            >

                                                                {Object.keys(
                                                                    predictions[0] ||
                                                                    {}
                                                                ).map(
                                                                    key => (

                                                                        <TableCell
                                                                            key={
                                                                                key
                                                                            }
                                                                        >
                                                                            {
                                                                                prediction?.[
                                                                                key
                                                                                ] ??
                                                                                "-"
                                                                            }
                                                                        </TableCell>

                                                                    )
                                                                )}

                                                            </TableRow>

                                                        )
                                                    )}

                                                </TableBody>

                                            </Table>

                                        </TableContainer>

                                    </Box>

                                )}

                        </TableContainer>

                    ) : (

                        <Typography
                            color="text.secondary"
                        >
                            {historyData.length === 0
                                ? "No Result Data"
                                : "Select a history run to view result."
                            }
                        </Typography>

                    )}

                </Paper>


                {/* =================================================
                    HISTORY
                ================================================== */}

                {showHistory && (

                    <Paper
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            p: 2,
                            minHeight: 300
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent:
                                    "space-between",
                                alignItems:
                                    "center",
                                mb: 2
                            }}
                        >

                            <Typography
                                variant="h6"
                            >
                                History
                            </Typography>


                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={
                                    <DeleteSweepIcon />
                                }
                                disabled={
                                    historyData.length ===
                                    0
                                }
                                onClick={() =>
                                    setOpenClearDialog(
                                        true
                                    )
                                }
                            >
                                Clear History
                            </Button>

                        </Box>


                        {historyData.length >
                            0 ? (

                            <TableContainer
                                sx={{
                                    maxHeight: 300,
                                    overflow:
                                        "auto"
                                }}
                            >

                                <Table
                                    size="small"
                                    stickyHeader
                                >

                                    <TableHead>

                                        <TableRow>

                                            <TableCell>
                                                <strong>
                                                    Run
                                                </strong>
                                            </TableCell>

                                            <TableCell>
                                                <strong>
                                                    Description
                                                </strong>
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                            >
                                                <strong>
                                                    Predict
                                                </strong>
                                            </TableCell>

                                        </TableRow>

                                    </TableHead>


                                    <TableBody>

                                        {historyData.map(
                                            entry => (

                                                <TableRow
                                                    key={
                                                        entry.run
                                                    }
                                                    hover
                                                    selected={
                                                        String(
                                                            selectedRun
                                                        ) ===
                                                        String(
                                                            entry.run
                                                        )
                                                    }
                                                    sx={{
                                                        cursor:
                                                            "pointer"
                                                    }}
                                                    onClick={() =>
                                                        handleSelectRun(
                                                            entry
                                                        )
                                                    }
                                                >

                                                    <TableCell>

                                                        <Link
                                                            component="button"
                                                            underline="hover"
                                                        >
                                                            {
                                                                entry.run
                                                            }
                                                        </Link>

                                                    </TableCell>


                                                    <TableCell>
                                                        {
                                                            entry.description
                                                        }
                                                    </TableCell>


                                                    <TableCell
                                                        align="center"
                                                    >

                                                        <Link
                                                            component="button"
                                                            underline="hover"
                                                            onClick={event =>
                                                                handlePredict(
                                                                    event,
                                                                    entry
                                                                )
                                                            }
                                                        >
                                                            Launch
                                                        </Link>

                                                    </TableCell>

                                                </TableRow>

                                            )
                                        )}

                                    </TableBody>

                                </Table>

                            </TableContainer>

                        ) : (

                            <Typography
                                color="text.secondary"
                            >
                                No History
                            </Typography>

                        )}

                    </Paper>

                )}

            </Box>


            {/* =================================================
                CALIBRATION GRAPH
            ================================================== */}

            {graphVisible && (

                <Paper
                    sx={{
                        width: "100%",
                        p: 2,
                        boxSizing: "border-box"
                    }}
                >

                    <Typography
                        variant="h6"
                        gutterBottom
                    >
                        Calibration Graph
                    </Typography>


                    {resp1Data.length === 0 &&
                        resp2Data.length === 0 ? (

                        <Typography
                            color="text.secondary"
                        >
                            No valid calibration response
                            data available for graph.
                        </Typography>

                    ) : (

                        <Box
                            sx={{
                                width: "100%",
                                minHeight: 450
                            }}
                        >

                            <HighchartsReact
                                highcharts={
                                    Highcharts
                                }
                                options={
                                    chartOptions
                                }
                                containerProps={{
                                    style: {
                                        width:
                                            "100%",
                                        height:
                                            "450px"
                                    }
                                }}
                            />

                        </Box>

                    )}

                </Paper>

            )}


            {/* =================================================
                CLEAR HISTORY DIALOG
            ================================================== */}

            <ConfirmDialog
                open={
                    openClearDialog
                }
                title="Clear History"
                message="Are you sure you want to clear all calibration history?"
                onConfirm={
                    handleClearHistory
                }
                onCancel={() =>
                    setOpenClearDialog(
                        false
                    )
                }
            />

        </Box>
    );
}
//import React, { useEffect, useState } from "react";
//import {
//    Box,
//    Table,
//    TableBody,
//    TableCell,
//    TableContainer,
//    TableHead,
//    TableRow,
//    Paper,
//    Link,
//    Typography,
//    Button
//} from "@mui/material";

//import Highcharts from "highcharts";
//import HighchartsReact from "highcharts-react-official";

//import ConfirmDialog from "../../Common/ConfirmDialog";

///**
// * MicroSlide history panel.
// *
// * Props:
// *   resultDataMap: {
// *      [runNumber]: {
// *          run,
// *          description,
// *          result,
// *          rows,
// *          parameters
// *      }
// *   }
// *
// *   resultFields: field names to display in the Result panel
// *   setResultDataMap: function to clear/update history
// *   showHistory: whether History grid should be displayed
// */
//export default function History({
//    resultDataMap,
//    setResultDataMap,
//    resultFields = [],
//    showHistory = true
//}) {
//    const [selectedRun, setSelectedRun] = useState(null);
//    const [openClearDialog, setOpenClearDialog] = useState(false);

//    const historyData = Object.values(resultDataMap || {});

//    /*
//     * Select the latest run whenever history changes.
//     */
//    useEffect(() => {
//        const runs = Object.keys(resultDataMap || {});

//        if (runs.length > 0) {
//            setSelectedRun(Number(runs[runs.length - 1]));
//        } else {
//            setSelectedRun(null);
//        }
//    }, [resultDataMap]);

//    /*
//     * Selected history entry.
//     */
//    const selectedEntry = resultDataMap?.[selectedRun];

//    const selectedResult = selectedEntry?.result;

//    const selectedDescription = selectedEntry?.description;

//    /*
//     * Calibration input rows for the selected run.
//     *
//     * Expected structure:
//     *
//     * [
//     *   {
//     *      sav: 15.5,
//     *      resp1: 100,
//     *      resp2: 105
//     *   },
//     *   ...
//     * ]
//     */
//    const selectedRows = Array.isArray(selectedEntry?.rows)
//        ? selectedEntry.rows
//        : [];

//    const fields = resultFields.length
//        ? resultFields
//        : Object.keys(selectedResult || {});

//    /*
//     * Prediction results.
//     */
//    const predictions = Array.isArray(selectedResult?.predictions)
//        ? selectedResult.predictions
//        : null;

//    /*
//     * Clear history.
//     */
//    const handleClearHistory = () => {
//        setResultDataMap({});

//        sessionStorage.removeItem("microSlideHistory");

//        setSelectedRun(null);
//        setOpenClearDialog(false);
//    };

//    /*
//     * Convert calibration rows into Highcharts data.
//     *
//     * X-axis = Response
//     * Y-axis = SAV
//     *
//     * Zero is a valid numeric value, so it must NOT be filtered
//     * using truthy checks.
//     */
//    const getNumericValue = (value) => {
//        if (
//            value === null ||
//            value === undefined ||
//            value === ""
//        ) {
//            return null;
//        }

//        const numberValue = Number(value);

//        return Number.isFinite(numberValue)
//            ? numberValue
//            : null;
//    };

//    const response1Data = selectedRows
//        .map((row) => {
//            const response = getNumericValue(row?.resp1);
//            const sav = getNumericValue(row?.sav);

//            if (response === null || sav === null) {
//                return null;
//            }

//            return {
//                x: response,
//                y: sav
//            };
//        })
//        .filter(Boolean);

//    const response2Data = selectedRows
//        .map((row) => {
//            const response = getNumericValue(row?.resp2);
//            const sav = getNumericValue(row?.sav);

//            if (response === null || sav === null) {
//                return null;
//            }

//            return {
//                x: response,
//                y: sav
//            };
//        })
//        .filter(Boolean);

//    /*
//     * Highcharts calibration graph.
//     */
//    const calibrationChartOptions = {
//        chart: {
//            type: "scatter",
//            height: 420,
//            zoomType: "xy"
//        },

//        title: {
//            text: "Calibration Curve"
//        },

//        subtitle: {
//            text: selectedDescription
//                ? `Run ${selectedRun} - ${selectedDescription}`
//                : selectedRun
//                    ? `Run ${selectedRun}`
//                    : ""
//        },

//        xAxis: {
//            title: {
//                text: "Response"
//            },

//            type: "linear",

//            gridLineWidth: 1
//        },

//        yAxis: {
//            title: {
//                text: "SAV"
//            },

//            type: "linear",

//            gridLineWidth: 1
//        },

//        tooltip: {
//            shared: false,

//            formatter: function () {
//                return `
//                    <b>${this.series.name}</b><br/>
//                    Response: ${this.x}<br/>
//                    SAV: ${this.y}
//                `;
//            }
//        },

//        legend: {
//            enabled: true
//        },

//        plotOptions: {
//            scatter: {
//                marker: {
//                    radius: 5,
//                    symbol: "circle"
//                }
//            },

//            series: {
//                animation: false
//            }
//        },

//        series: [
//            {
//                name: "Response 1",
//                data: response1Data
//            },
//            {
//                name: "Response 2",
//                data: response2Data
//            }
//        ],

//        credits: {
//            enabled: false
//        },

//        exporting: {
//            enabled: false
//        }
//    };

//    return (
//        <>
//            <Box
//                sx={{
//                    display: "flex",
//                    gap: 2,
//                    flexDirection: {
//                        xs: "column",
//                        md: "row"
//                    }
//                }}
//            >
//                {/* =========================
//                    RESULT PANEL
//                ========================== */}
//                <Paper
//                    sx={{
//                        flex: showHistory ? 1 : "none",
//                        width: showHistory
//                            ? "auto"
//                            : "100%",
//                        p: 2,
//                        minHeight: 300
//                    }}
//                >
//                    <Typography
//                        variant="h6"
//                        gutterBottom
//                    >
//                        Result{" "}
//                        {selectedRun
//                            ? `(${selectedDescription})`
//                            : ""}
//                    </Typography>

//                    {selectedResult ? (
//                        <>
//                            {/* =========================
//                                RESULT TABLE
//                            ========================== */}
//                            <Table size="small">
//                                <TableBody>
//                                    {fields.map((field) => (
//                                        <TableRow key={field}>
//                                            <TableCell>
//                                                <strong>
//                                                    {field}
//                                                </strong>
//                                            </TableCell>

//                                            <TableCell>
//                                                {selectedResult?.[
//                                                    field
//                                                ] ?? "-"}
//                                            </TableCell>
//                                        </TableRow>
//                                    ))}
//                                </TableBody>
//                            </Table>

//                            {/* =========================
//                                PREDICTION RESULTS
//                            ========================== */}
//                            {predictions &&
//                                predictions.length > 0 && (
//                                    <TableContainer
//                                        sx={{ mt: 2 }}
//                                    >
//                                        <Table size="small">
//                                            <TableHead>
//                                                <TableRow>
//                                                    <TableCell>
//                                                        <strong>
//                                                            Sample
//                                                        </strong>
//                                                    </TableCell>

//                                                    <TableCell>
//                                                        <strong>
//                                                            Resp1
//                                                        </strong>
//                                                    </TableCell>

//                                                    <TableCell>
//                                                        <strong>
//                                                            Resp2
//                                                        </strong>
//                                                    </TableCell>

//                                                    <TableCell>
//                                                        <strong>
//                                                            Concentration
//                                                        </strong>
//                                                    </TableCell>

//                                                    <TableCell>
//                                                        <strong>
//                                                            Cond
//                                                        </strong>
//                                                    </TableCell>
//                                                </TableRow>
//                                            </TableHead>

//                                            <TableBody>
//                                                {predictions.map(
//                                                    (p, i) => (
//                                                        <TableRow
//                                                            key={i}
//                                                        >
//                                                            <TableCell>
//                                                                {
//                                                                    p.sampleId
//                                                                }{" "}
//                                                                ||
//                                                                {
//                                                                    i
//                                                                }
//                                                            </TableCell>

//                                                            <TableCell>
//                                                                {p.response1 ??
//                                                                    "-"}
//                                                            </TableCell>

//                                                            <TableCell>
//                                                                {p.response2 ??
//                                                                    "-"}
//                                                            </TableCell>

//                                                            <TableCell>
//                                                                {p.concentration ??
//                                                                    "-"}
//                                                            </TableCell>

//                                                            <TableCell>
//                                                                {p.conditionCode ??
//                                                                    ""}
//                                                            </TableCell>
//                                                        </TableRow>
//                                                    )
//                                                )}
//                                            </TableBody>
//                                        </Table>
//                                    </TableContainer>
//                                )}

//                            {/* =========================
//                                CALIBRATION GRAPH
//                            ========================== */}
//                            {selectedEntry &&
//                                selectedRows.length > 0 &&
//                                (response1Data.length > 0 ||
//                                    response2Data.length > 0) && (
//                                    <Box
//                                        sx={{
//                                            mt: 3,
//                                            width: "100%"
//                                        }}
//                                    >
//                                        <HighchartsReact
//                                            highcharts={Highcharts}
//                                            options={calibrationChartOptions}
//                                        />
//                                    </Box>
//                                )}
                           
//                        </>
//                    ) : (
//                        <Typography>
//                            No Result Data
//                        </Typography>
//                    )}
//                </Paper>

//                {/* =========================
//                    HISTORY PANEL
//                ========================== */}
//                {showHistory && (
//                    <Paper
//                        sx={{
//                            flex: 1,
//                            p: 2,
//                            minHeight: 300
//                        }}
//                    >
//                        {/* History Header */}
//                        <Box
//                            sx={{
//                                display: "flex",
//                                alignItems: "center",
//                                justifyContent:
//                                    "space-between",
//                                mb: 1
//                            }}
//                        >
//                            <Typography variant="h6">
//                                History
//                            </Typography>

//                            <Button
//                                variant="outlined"
//                                color="error"
//                                size="small"
//                                disabled={
//                                    historyData.length === 0
//                                }
//                                onClick={() =>
//                                    setOpenClearDialog(
//                                        true
//                                    )
//                                }
//                            >
//                                Clear History
//                            </Button>
//                        </Box>

//                        {historyData.length > 0 ? (
//                            <TableContainer>
//                                <Table size="small">
//                                    <TableHead>
//                                        <TableRow>
//                                            <TableCell>
//                                                <strong>
//                                                    Run
//                                                </strong>
//                                            </TableCell>

//                                            <TableCell>
//                                                <strong>
//                                                    Description
//                                                </strong>
//                                            </TableCell>
//                                        </TableRow>
//                                    </TableHead>

//                                    <TableBody>
//                                        {historyData.map(
//                                            (entry) => (
//                                                <TableRow
//                                                    key={
//                                                        entry.run
//                                                    }
//                                                    hover
//                                                    selected={
//                                                        selectedRun ===
//                                                        entry.run
//                                                    }
//                                                    sx={{
//                                                        cursor:
//                                                            "pointer"
//                                                    }}
//                                                    onClick={() =>
//                                                        setSelectedRun(
//                                                            entry.run
//                                                        )
//                                                    }
//                                                >
//                                                    <TableCell>
//                                                        <Link
//                                                            component="button"
//                                                            underline="hover"
//                                                        >
//                                                            {
//                                                                entry.run
//                                                            }
//                                                        </Link>
//                                                    </TableCell>

//                                                    <TableCell>
//                                                        {
//                                                            entry.description
//                                                        }
//                                                    </TableCell>
//                                                </TableRow>
//                                            )
//                                        )}
//                                    </TableBody>
//                                </Table>
//                            </TableContainer>
//                        ) : (
//                            <Typography>
//                                No History
//                            </Typography>
//                        )}
//                    </Paper>
//                )}
//            </Box>

//            {/* =========================
//                CLEAR HISTORY CONFIRMATION
//            ========================== */}
//            <ConfirmDialog
//                open={openClearDialog}
//                title="Clear History"
//                message="Are you sure you want to clear all history?"
//                onConfirm={handleClearHistory}
//                onCancel={() =>
//                    setOpenClearDialog(false)
//                }
//            />
//        </>
//    );
//}

//import React, { useEffect, useState } from "react";
//import {
//    Box,
//    Table,
//    TableBody,
//    TableCell,
//    TableContainer,
//    TableHead,
//    TableRow,
//    Paper,
//    Link,
//    Typography,
//    Button
//} from "@mui/material";

//import ConfirmDialog from "../../Common/ConfirmDialog";

///**
// * MicroSlide history panel.
// *
// * Props:
// *   resultDataMap: { [runNumber]: { run, description, result, rows, parameters } }
// *   resultFields: field names to display in the Result panel
// *   setResultDataMap: function to clear/update history
// */
//export default function History({
//    resultDataMap,
//    setResultDataMap,
//    resultFields = [],
//    showHistory = true
//}) {
//    const [selectedRun, setSelectedRun] = useState(null);
//    const [openClearDialog, setOpenClearDialog] = useState(false);

//    const historyData = Object.values(resultDataMap || {});

//    useEffect(() => {
//        const runs = Object.keys(resultDataMap || {});

//        if (runs.length > 0) {
//            setSelectedRun(Number(runs[runs.length - 1]));
//        } else {
//            setSelectedRun(null);
//        }
//    }, [resultDataMap]);

//    const selectedEntry = resultDataMap?.[selectedRun];
//    const selectedResult = selectedEntry?.result;
//    const selectedDescription = selectedEntry?.description;

//    const fields = resultFields.length
//        ? resultFields
//        : Object.keys(selectedResult || {});

//    // Predict batches put their per-sample rows in result.predictions;
//    // render those as a separate detail table.
//    const predictions = Array.isArray(selectedResult?.predictions)
//        ? selectedResult.predictions
//        : null;

//    const handleClearHistory = () => {
//        setResultDataMap({});

//        // Clear MicroSlide history from sessionStorage if it is stored there.
//        sessionStorage.removeItem("microSlideHistory");

//        setSelectedRun(null);
//        setOpenClearDialog(false);
//    };

//    return (
//        <>
//            <Box
//                sx={{
//                    display: "flex",
//                    gap: 2,
//                    flexDirection: { xs: "column", md: "row" }
//                }}
//            >
//                <Paper
//                    sx={{
//                        flex: showHistory ? 1 : "none",
//                        width: showHistory ? "auto" : "100%",
//                        p: 2,
//                        minHeight: 300
//                    }}
//                >
//                    <Typography variant="h6" gutterBottom>
//                        Result {selectedRun ? `(${ selectedDescription })` : ""}
//                    </Typography>

//                    {selectedResult ? (
//                        <>
//                            <Table size="small">
//                                <TableBody>
//                                    {fields.map(field => (
//                                        <TableRow key={field}>
//                                            <TableCell>
//                                                <strong>{field}</strong>
//                                            </TableCell>
//                                            <TableCell>
//                                                {selectedResult?.[field] ?? "-"}
//                                            </TableCell>
//                                        </TableRow>
//                                    ))}
//                                </TableBody>
//                            </Table>

//                            {predictions && predictions.length > 0 && (
//                                <TableContainer sx={{ mt: 2 }}>
//                                    <Table size="small">
//                                        <TableHead>
//                                            <TableRow>
//                                                <TableCell>
//                                                    <strong>Sample</strong>
//                                                </TableCell>
//                                                <TableCell>
//                                                    <strong>Resp1</strong>
//                                                </TableCell>
//                                                <TableCell>
//                                                    <strong>Resp2</strong>
//                                                </TableCell>
//                                                <TableCell>
//                                                    <strong>Concentration</strong>
//                                                </TableCell>
//                                                <TableCell>
//                                                    <strong>Cond</strong>
//                                                </TableCell>
//                                            </TableRow>
//                                        </TableHead>

//                                        <TableBody>
//                                            {predictions.map((p, i) => (
//                                                <TableRow key={i}>
//                                                    <TableCell>
//                                                        {p.sampleId || i + 1}
//                                                    </TableCell>
//                                                    <TableCell>
//                                                        {p.response1 ?? "-"}
//                                                    </TableCell>
//                                                    <TableCell>
//                                                        {p.response2 ?? "-"}
//                                                    </TableCell>
//                                                    <TableCell>
//                                                        {p.concentration ?? "-"}
//                                                    </TableCell>
//                                                    <TableCell>
//                                                        {p.conditionCode ?? ""}
//                                                    </TableCell>
//                                                </TableRow>
//                                            ))}
//                                        </TableBody>
//                                    </Table>
//                                </TableContainer>
//                            )}
//                        </>
//                    ) : (
//                        <Typography>No Result Data</Typography>
//                    )}
//                </Paper>
//                {showHistory && (
//                    <Paper sx={{ flex: 1, p: 2, minHeight: 300 }}>
//                        {/* History Header */}
//                        <Box
//                            sx={{
//                                display: "flex",
//                                alignItems: "center",
//                                justifyContent: "space-between",
//                                mb: 1
//                            }}
//                        >
//                            <Typography variant="h6">
//                                History
//                            </Typography>

//                            <Button
//                                variant="outlined"
//                                color="error"
//                                size="small"
//                                disabled={historyData.length === 0}
//                                onClick={() => setOpenClearDialog(true)}
//                            >
//                                Clear History
//                            </Button>
//                        </Box>

//                        {historyData.length > 0 ? (
//                            <TableContainer>
//                                <Table size="small">
//                                    <TableHead>
//                                        <TableRow>
//                                            <TableCell>
//                                                <strong>Run</strong>
//                                            </TableCell>

//                                            <TableCell>
//                                                <strong>Description</strong>
//                                            </TableCell>
//                                        </TableRow>
//                                    </TableHead>

//                                    <TableBody>
//                                        {historyData.map(entry => (
//                                            <TableRow
//                                                key={entry.run}
//                                                hover
//                                                selected={selectedRun === entry.run}
//                                                sx={{ cursor: "pointer" }}
//                                                onClick={() =>
//                                                    setSelectedRun(entry.run)
//                                                }
//                                            >
//                                                <TableCell>
//                                                    <Link
//                                                        component="button"
//                                                        underline="hover"
//                                                    >
//                                                        {entry.run}
//                                                    </Link>
//                                                </TableCell>

//                                                <TableCell>
//                                                    {entry.description}
//                                                </TableCell>
//                                            </TableRow>
//                                        ))}
//                                    </TableBody>
//                                </Table>
//                            </TableContainer>
//                        ) : (
//                            <Typography>
//                                No History
//                            </Typography>
//                        )}
//                    </Paper>
//                )}
          
//            </Box>

//            {/* Clear History Confirmation */}
//            <ConfirmDialog
//                open={openClearDialog}
//                title="Clear History"
//                message="Are you sure you want to clear all history?"
//                onConfirm={handleClearHistory}
//                onCancel={() => setOpenClearDialog(false)}
//            />
//        </>
//    );
//}
